/**
 * VorlagenEditor — Texte je Dokumenttyp (Admin → PDF-Einstellungen → Texte).
 * Links Typ-Auswahl (mobil: Raster, kein horizontales Scrollen), rechts Felder,
 * Platzhalter-Chips (Klick fügt ein), „Auf Standard zurücksetzen“ je Typ.
 * Arbeitet unveränderlich auf dem settings-Objekt (vorlagen-JSON).
 */
import { useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  DOKUMENT_TYPEN, VORLAGEN_FELDER, getVorlage, platzhalterFuer, setVorlagenFeld, resetVorlage, isVorlageAngepasst,
} from '@/lib/dokumentVorlagen';

const GRUPPEN = [...new Set(DOKUMENT_TYPEN.map((t) => t.gruppe))];

export default function VorlagenEditor({ settings, onChange, typ, onTypChange }) {
  const [aktivesFeld, setAktivesFeld] = useState(null);
  const refs = useRef({});
  const felder = VORLAGEN_FELDER[typ] || [];
  const werte = getVorlage(settings, typ);
  const angepasst = isVorlageAngepasst(settings, typ);
  const typDef = DOKUMENT_TYPEN.find((t) => t.id === typ);

  const setFeld = (key, value) => onChange(setVorlagenFeld(settings, typ, key, value));

  /** Platzhalter an der Cursorposition des zuletzt fokussierten Feldes einfügen. */
  const einfuegen = (platzhalter) => {
    const key = aktivesFeld || felder.find((f) => f.typ !== 'number')?.key;
    if (!key) return;
    const el = refs.current[key];
    const current = werte[key] || '';
    const token = `{{${platzhalter}}}`;
    if (el && typeof el.selectionStart === 'number') {
      const a = el.selectionStart; const b = el.selectionEnd;
      setFeld(key, current.slice(0, a) + token + current.slice(b));
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + token.length, a + token.length); });
    } else {
      setFeld(key, current + token);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
      <nav aria-label="Dokumenttyp" className="space-y-3">
        {GRUPPEN.map((gruppe) => (
          <div key={gruppe}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400 mb-1">{gruppe}</p>
            <div className="grid grid-cols-2 gap-1 lg:grid-cols-1">
              {DOKUMENT_TYPEN.filter((t) => t.gruppe === gruppe).map((t) => {
                const active = t.id === typ;
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onTypChange(t.id)}
                    className={cn(
                      'flex items-center justify-between gap-2 min-h-[44px] lg:min-h-[40px] px-3 py-1.5 rounded-lg text-[13px] sm:text-sm leading-tight text-left transition-colors',
                      active ? 'bg-brand-950 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                    )}
                  >
                    <span className="min-w-0">{t.label}</span>
                    {isVorlageAngepasst(settings, t.id) && (
                      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', active ? 'bg-accent' : 'bg-brand-700')} title="angepasst" aria-label="angepasst" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="min-w-0 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-display text-base font-bold text-slate-900">{typDef?.label}</h3>
            <p className="text-xs text-slate-500">{angepasst ? 'Eigene Texte gespeichert' : 'Standardtexte (noch nicht angepasst)'}</p>
          </div>
          <button
            type="button"
            onClick={() => onChange(resetVorlage(settings, typ))}
            disabled={!angepasst}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Auf Standard zurücksetzen
          </button>
        </div>

        <div className="space-y-4">
          {felder.map((feld) => {
            const id = `vorlage-${typ}-${feld.key}`;
            const common = {
              id,
              ref: (el) => { refs.current[feld.key] = el; },
              value: werte[feld.key] ?? '',
              onFocus: () => setAktivesFeld(feld.key),
              onChange: (e) => setFeld(feld.key, e.target.value),
              placeholder: feld.standard,
            };
            return (
              <div key={feld.key}>
                <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{feld.label}</label>
                {feld.typ === 'textarea'
                  ? <Textarea {...common} rows={feld.key === 'text' ? 6 : 3} className="text-sm leading-relaxed" />
                  : <Input {...common} type={feld.typ === 'number' ? 'number' : 'text'} inputMode={feld.typ === 'number' ? 'numeric' : undefined} className="h-11 text-sm" />}
                {feld.hilfe && <p className="mt-1 text-[11px] text-slate-400">{feld.hilfe}</p>}
              </div>
            );
          })}
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400 mb-2">Platzhalter einfügen</p>
          <div className="flex flex-wrap gap-1.5">
            {platzhalterFuer(typ).map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => einfuegen(p.key)}
                title={`${p.label} – z. B. ${p.beispiel}`}
                className="inline-flex min-h-[36px] items-center rounded-full border border-slate-200 bg-slate-50 px-3 font-mono text-[11px] text-slate-700 hover:border-brand-700 hover:text-brand-900 transition-colors"
              >
                {`{{${p.key}}}`}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Fett: **Text**. Zeilenumbrüche werden übernommen. HTML wird nicht interpretiert.</p>
        </div>
      </div>
    </div>
  );
}
